/**
 * Offline status — observable state for UI to show sync indicators.
 */

import { offlineQueue } from './offlineQueue';
import { createLogger } from './logger';

const log = createLogger('OFFLINE_STATUS');

export type SyncStatus = 'idle' | 'pending' | 'syncing' | 'synced' | 'error';

let currentStatus: SyncStatus = 'idle';
let pendingCount = 0;
const listeners = new Set<(status: SyncStatus, pending: number) => void>();

export function getSyncStatus(): { status: SyncStatus; pending: number } {
  return { status: currentStatus, pending: pendingCount };
}

export function onSyncStatusChange(listener: (status: SyncStatus, pending: number) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notify() {
  for (const l of listeners) l(currentStatus, pendingCount);
}

export function setSyncStatus(status: SyncStatus, pending: number = 0) {
  currentStatus = status;
  pendingCount = pending;
  log.info('Sync status:', status, 'pending:', pending);
  notify();
}

/**
 * Check pending count and update status.
 * Call this after any save or sync operation.
 */
export async function refreshSyncStatus(): Promise<void> {
  const count = await offlineQueue.getPendingCount();
  if (count > 0) {
    setSyncStatus('pending', count);
  } else if (currentStatus === 'syncing') {
    setSyncStatus('synced', 0);
    // Reset to idle after 3 seconds
    setTimeout(() => {
      if (currentStatus === 'synced') setSyncStatus('idle', 0);
    }, 3000);
  } else {
    setSyncStatus('idle', 0);
  }
}
